import React, { useState, useEffect } from "react";
import {
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Box,
  Button,
  Typography,
  IconButton,
} from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import axios from "axios";
import { BACKEND_BASE_URL } from "../../config";

const notificationOptions = [
  { label: "Email", value: "email" },
  { label: "Whatsapp", value: "whatsapp" },
  { label: "Both", value: "both" },
  { label: "None", value: "none" },
];

const API_BASE_URL = `${BACKEND_BASE_URL}/api`;

const PlanForm = ({ initialData, onSubmit, onCancel, currencyOptions: propCurrencyOptions }) => {


  // Function to infer plan_mode from plan_type
  const inferPlanMode = (planType) => {
    if (["base", "gold", "silver", "platinum"].includes(planType)) {
      return "individual";
    }
    if (["team_3", "team_5"].includes(planType)) {
      return "team";
    }
    if (planType === "enterprise") {
      return "enterprise";
    }
    if (planType === "addon") {
      return "addon";
    }
    return "individual"; // default fallback
  };

  // Transform initialData prices to match form structure
  const transformInitialData = (data) => {
    if (!data) return {};

    const transformed = { ...data };

    // Infer plan_mode if it's missing
    if (!transformed.plan_mode && transformed.plan_type) {
      transformed.plan_mode = inferPlanMode(transformed.plan_type);
    }

    // Transform prices array if it exists
    if (data.prices && Array.isArray(data.prices)) {
      transformed.prices = data.prices.map(price => ({
        currency: price.currency,
        price_monthly: price.monthly_price || "",
        price_yearly: price.yearly_price || ""
      }));
    }

    return transformed;
  };

  const [formData, setFormData] = useState({
    name: "",
    plan_type: "",
    plan_mode: "",
    allowed_countries: 1,       // New
    is_most_popular: false, // New checkbox
    unlimited_countries: false, // New
    allowed_towns: 0,
    unlimited_towns: false,
    aware_explain_limit: 0,
    aware_ask_limit: 0,
    max_users: 1,
    shared_dashboard: false,
    admin_tools: false,
    priority_support: false,
    dedicated_support: false,
    notification_preference: "email",
    is_active: true,
    is_addon: false,
    is_enterprise: false,
    trial_days: 0,
    prices: [
      // default to first currency
      { currency: propCurrencyOptions?.[0]?.value || "USD", price_monthly: "", price_yearly: "" },
    ],
    ...transformInitialData(initialData),
  });

  const [planTypeOptions, setPlanTypeOptions] = useState([]);
  const [planModeOptions, setPlanModeOptions] = useState([]);
  const [fetchedCurrencyOptions, setFetchedCurrencyOptions] = useState([]);

  // Use prop currency options if available, otherwise use fetched ones
  const currencyOptions = propCurrencyOptions || fetchedCurrencyOptions;

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/plan-types/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      })
      .then((res) => {
        // Merge only non-addon plan types
        const mergedPlanTypes = [
          ...(res.data.individual_plan_types || []),
          ...(res.data.team_plan_types || []),
          ...(res.data.enterprise_plan_types || []),
          // ❌ Removed addon_plan_types
        ];

        setPlanTypeOptions(mergedPlanTypes);
        setPlanModeOptions((res.data.plan_modes || []).filter(mode => mode.value !== "addon"));
        setFetchedCurrencyOptions(res.data.currencies || []);

        setFormData((prev) => {
          let updated = { ...prev };

          // Only set defaults if no initial data was provided
          if (!initialData) {
            if (!prev.plan_type && mergedPlanTypes.length > 0) {
              updated.plan_type = mergedPlanTypes[0].value;
            }
            if (!prev.plan_mode && res.data.plan_modes.length > 0) {
              updated.plan_mode = res.data.plan_modes[0].value;
            }
            // Only set currency if we don't have prop currency options and no existing currency
            if (!propCurrencyOptions && !prev.prices?.[0]?.currency && res.data.currencies.length > 0) {
              updated.prices = [
                { currency: res.data.currencies[0].value, price_monthly: "", price_yearly: "" }
              ];
            }
          } else {
            // For existing data, ensure plan_mode is set correctly
            if (updated.plan_type && !updated.plan_mode) {
              updated.plan_mode = inferPlanMode(updated.plan_type);
            }
          }

          return updated;
        });
      })
      .catch((err) => {
        console.error("Error fetching plan types/modes:", err);
      });
  }, [propCurrencyOptions]);

  // Fixed filtering logic for addon and enterprise
  const filteredPlanTypes = planTypeOptions.filter((type) => {
    if (formData.plan_mode === "individual") {
      return ["base", "gold", "silver", "platinum"].includes(type.value);
    }
    if (formData.plan_mode === "team") {
      return ["team_3", "team_5"].includes(type.value);
    }
    if (formData.plan_mode === "enterprise") {
      return type.value === "enterprise";
    }
    if (formData.plan_mode === "addon") {
      return type.value === "addon";
    }
    return true;
  });

  const handleChange = (e, index, field) => {
    const { name, value, type, checked } = e.target;
    if (field === "prices") {
      const updatedPrices = [...formData.prices];
      updatedPrices[index][name] = value;
      setFormData({ ...formData, prices: updatedPrices });
      return;
    }
    const newValue = type === "checkbox" ? checked : value;

    // Auto-set max_users for team plan types
    if (name === "plan_type") {
      if (value === "team_3") return setFormData((prev) => ({ ...prev, [name]: value, max_users: 3 }));
      if (value === "team_5") return setFormData((prev) => ({ ...prev, [name]: value, max_users: 5 }));
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleAddCurrency = () => {
    const availableCurrencies = currencyOptions.filter(
      curr => !formData.prices.some(price => price.currency === curr.value)
    );

    const availableCurrency = availableCurrencies.length > 0 ? availableCurrencies[0].value : "USD";

    setFormData((prev) => ({
      ...prev,
      prices: [...prev.prices, { currency: availableCurrency, price_monthly: "", price_yearly: "" }],
    }));
  };

  const handleRemoveCurrency = (index) => {
    if (formData.prices.length > 1) {
      const updatedPrices = [...formData.prices];
      updatedPrices.splice(index, 1);
      setFormData({ ...formData, prices: updatedPrices });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { ...formData };

    // ✅ If unlimited_towns, don't send leftover empty string
    if (payload.unlimited_towns) {
      payload.allowed_towns = 0;
    }
    if (payload.unlimited_countries) {
      payload.allowed_countries = 0;
    }

    // Transform prices back to API format
    payload.prices = payload.prices.map(price => ({
      currency: price.currency,
      monthly_price: price.price_monthly,
      yearly_price: price.price_yearly
    }));

    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        label="Plan Name"
        name="name"
        value={formData.name}
        onChange={handleChange}
        fullWidth
        margin="normal"
        required
      />

      <TextField
        select
        label="Plan Mode"
        name="plan_mode"
        value={formData.plan_mode}
        onChange={handleChange}
        fullWidth
        margin="normal"
      >
        {planModeOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Plan Type"
        name="plan_type"
        value={formData.plan_type}
        onChange={handleChange}
        fullWidth
        margin="normal"
      >
        {filteredPlanTypes.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <Box mt={2}>
        <Typography variant="subtitle1">Pricing per Currency</Typography>
        {formData.prices.map((price, index) => (
          <Box key={index} display="flex" alignItems="center" gap={2} mt={1}>
            <TextField
              select
              label="Currency"
              name="currency"
              value={price.currency}
              onChange={(e) => handleChange(e, index, "prices")}
              sx={{ minWidth: 120 }}
            >
              {currencyOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Monthly Price"
              name="price_monthly"
              type="number"
              value={price.price_monthly}
              onChange={(e) => handleChange(e, index, "prices")}
              required
            />

            <TextField
              label="Yearly Price"
              name="price_yearly"
              type="number"
              value={price.price_yearly}
              onChange={(e) => handleChange(e, index, "prices")}
            />

            {formData.prices.length > 1 && (
              <IconButton color="error" onClick={() => handleRemoveCurrency(index)}>
                <RemoveCircleOutline />
              </IconButton>
            )}
          </Box>
        ))}
        <Button startIcon={<AddCircleOutline />} onClick={handleAddCurrency} sx={{ mt: 1 }}>
          Add Currency
        </Button>
      </Box>

      <TextField
        label="Trial Days"
        name="trial_days"
        type="number"
        value={formData.trial_days}
        onChange={handleChange}
        fullWidth
        margin="normal"
      />



      <TextField
        label="Allowed Countries"
        name="allowed_countries"
        type="number"
        value={formData.allowed_countries}
        onChange={handleChange}
        fullWidth
        margin="normal"
        disabled={formData.unlimited_countries}
      />
      <Box mt={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.unlimited_countries || false}
              onChange={handleChange}
              name="unlimited_countries"
            />
          }
          label="Unlimited Countries"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.is_most_popular}
              onChange={handleChange}
              name="is_most_popular"
            />
          }
          label="Most Valued Plan"
        />

      </Box>



      <TextField
        label="Allowed Towns"
        name="allowed_towns"
        type="number"
        value={formData.allowed_towns}
        onChange={handleChange}
        fullWidth
        margin="normal"
        disabled={formData.unlimited_towns}
      />
      <Box mt={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.unlimited_towns || false}
              onChange={handleChange}
              name="unlimited_towns"
            />
          }
          label="Unlimited Towns"
        />
      </Box>

      <TextField
        label="Explain Limit"
        name="aware_explain_limit"
        type="number"
        value={formData.aware_explain_limit}
        onChange={handleChange}
        fullWidth
        margin="normal"
        required
      />

      <TextField
        label="Ask Limit"
        name="aware_ask_limit"
        type="number"
        value={formData.aware_ask_limit}
        onChange={handleChange}
        fullWidth
        margin="normal"
        required
      />

      <TextField
        label="Max Users"
        name="max_users"
        type="number"
        value={formData.max_users}
        onChange={handleChange}
        fullWidth
        margin="normal"
        required
      />

      {/* <Box mt={2}>
        <FormControlLabel
          control={<Checkbox checked={formData.shared_dashboard} onChange={handleChange} name="shared_dashboard" />}
          label="Shared Dashboard"
        />
        <FormControlLabel
          control={<Checkbox checked={formData.admin_tools} onChange={handleChange} name="admin_tools" />}
          label="Admin Tools"
        />
        <FormControlLabel
          control={<Checkbox checked={formData.priority_support} onChange={handleChange} name="priority_support" />}
          label="Priority Support"
        />
        <FormControlLabel
          control={<Checkbox checked={formData.dedicated_support} onChange={handleChange} name="dedicated_support" />}
          label="Dedicated Support"
        />
      </Box> */}

      <TextField
        select
        label="Notification Preference"
        name="notification_preference"
        value={formData.notification_preference}
        onChange={handleChange}
        fullWidth
        margin="normal"
      >
        {notificationOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <Box mt={2}>
        <FormControlLabel
          control={<Checkbox checked={formData.is_active} onChange={handleChange} name="is_active" />}
          label="Is Active"
        />
        {/* <FormControlLabel
          control={<Checkbox checked={formData.is_addon} onChange={handleChange} name="is_addon" />}
          label="Is Addon"
        /> */}
        {/* <FormControlLabel
          control={<Checkbox checked={formData.is_enterprise} onChange={handleChange} name="is_enterprise" />}
          label="Is Enterprise"
        /> */}
      </Box>

      <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" type="submit">
          Save
        </Button>
      </Box>
    </form>
  );
};

export default PlanForm;